import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { shareReplay } from 'rxjs';

import { filterDefined } from '@core/common-legacy';
import { fetchIfNotLoaded } from '@core/ngrx-legacy';
import type { SeatKind, TellentSubscriptionStatus } from '@core/tellent-types';

import { TellentAppsActions, TellentAppsSelectors } from './tellent-apps.store';
import { OrganizationChannelActions } from '../../organization';

@Injectable({
  providedIn: 'root',
})
export class TellentAppsDataService {
  private readonly actions$ = inject(Actions);
  public store = inject(Store);

  public apps$ = this.store.pipe(
    this.fetchIfNotLoaded(),
    select(TellentAppsSelectors.allEntities),
    filterDefined,
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public appsLoaded$ = this.store.pipe(
    select(TellentAppsSelectors.isLoaded()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    this.setupRefresh();
  }

  public getAppBySeatKind(kind: SeatKind) {
    return this.store.pipe(
      this.fetchIfNotLoaded(),
      select(TellentAppsSelectors.selectAppBySeatKind(kind)),
      filterDefined,
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public getAppBySeatKindIfExist(kind: SeatKind) {
    return this.store.pipe(
      this.fetchIfNotLoaded(),
      select(TellentAppsSelectors.selectAppBySeatKind(kind)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public hasActiveTrialAccess(kind: SeatKind) {
    return this.store.pipe(
      this.fetchIfNotLoaded(),
      select(
        TellentAppsSelectors.selectHasSubscriptionStatus({ seatKind: kind, status: 'trialing' }),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public hasActiveSubscriptionAccess(kind: SeatKind) {
    return this.store.pipe(
      this.fetchIfNotLoaded(),
      select(
        TellentAppsSelectors.selectHasSubscriptionStatus({ seatKind: kind, status: 'active' }),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public hasInactiveSubscription(kind: SeatKind) {
    return this.store.pipe(
      this.fetchIfNotLoaded(),
      select(
        TellentAppsSelectors.selectHasSubscriptionStatus({ seatKind: kind, status: 'inactive' }),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public hasAnySubscriptionStatus(kind: SeatKind, statuses: TellentSubscriptionStatus[]) {
    return this.store.pipe(
      this.fetchIfNotLoaded(),
      select(TellentAppsSelectors.selectHasAnySubscriptionStatus({ seatKind: kind, statuses })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private fetchIfNotLoaded() {
    return fetchIfNotLoaded(this.store.select(TellentAppsSelectors.meta()), () =>
      this.store.dispatch(TellentAppsActions.fetchCollection()),
    );
  }

  private setupRefresh() {
    this.actions$
      .pipe(ofType(OrganizationChannelActions.subscriptionUpdated))
      .subscribe(() => this.store.dispatch(TellentAppsActions.fetchCollection()));
  }
}
